/**
 * 来客受付の設定 `/reception/admin/settings`(設計書 §7.3)。専用の口 `/api/reception/admin/settings/{key}` を使う
 * (汎用の `/api/core/settings` は reception.* を受けない)。
 * 書ける人: 保持期間(reception.retention)と同一オリジン例外(kiosk.same_origin_exception)は suite:admin、
 * ほかは reception:admin。文字の欄を空にして保存すると既定に戻す(DELETE)。
 */
import { useMutation } from "@tanstack/react-query";
import { Alert, Button, Card, Form, Input, InputNumber, Select, Switch, Typography, message } from "antd";
import { useEffect } from "react";

import { PageHeader } from "../../ui";
import {
  errorText,
  useInvalidateReception,
  useReceptionPerms,
  useSend,
  useSettings,
  type ReceptionSetting,
} from "./hooks";

const { Text } = Typography;

type Field =
  | { name: string; label: string; kind: "text"; extra?: string; placeholder?: string }
  | { name: string; label: string; kind: "int"; min: number; max: number; unit: string; extra?: string }
  | { name: string; label: string; kind: "bool"; extra?: string }
  | { name: string; label: string; kind: "select"; options: { value: string; label: string }[]; extra?: string };

const SECTIONS: { key: string; title: string; description?: string; fields: Field[] }[] = [
  {
    key: "reception.general",
    title: "全般",
    fields: [
      {
        name: "kiosk_origin",
        label: "受付端末のオリジン",
        kind: "text",
        placeholder: "https://reception.example.co.jp",
        extra:
          "案内用の表示。端末の口が実際に開くのは api の環境変数 RECEPTION_KIOSK_HOST のホストで、それと同じホストの https オリジンでないと保存できない(スイートと同じホストも不可)",
      },
      {
        name: "default_locale",
        label: "受付画面の既定の言語",
        kind: "select",
        options: [
          { value: "ja", label: "日本語" },
          { value: "en", label: "英語" },
        ],
      },
    ],
  },
  {
    key: "reception.mail",
    title: "メール",
    description: "来訪者・担当者・受付窓口へのメール。SMTP の接続先は全体の設定を使う。",
    fields: [
      {
        name: "bounce_mailbox",
        label: "バウンスの受け箱(エンベロープ送信者)",
        kind: "text",
        placeholder: "reception-bounce@example.co.jp",
      },
      { name: "from_name", label: "差出人の表示名", kind: "text", placeholder: "受付" },
    ],
  },
  {
    key: "reception.kiosk",
    title: "受付端末",
    fields: [
      { name: "token_ttl_days", label: "端末の資格の有効期間", kind: "int", min: 1, max: 365, unit: "日" },
      {
        name: "rotate_every_days",
        label: "資格を入れ替える間隔",
        kind: "int",
        min: 1,
        max: 365,
        unit: "日",
        extra: "有効期間より短くする",
      },
      { name: "offline_max_hours", label: "オフラインで使える上限", kind: "int", min: 1, max: 168, unit: "時間" },
      { name: "idle_seconds", label: "操作が無いとき最初の画面に戻すまで", kind: "int", min: 10, max: 3600, unit: "秒" },
      {
        name: "same_origin_exception",
        label: "同一オリジンの例外(縮退構成)",
        kind: "bool",
        extra: "受付端末を管理画面と同じオリジンで動かす。suite:admin だけが変えられ、理由が監査に残る",
      },
    ],
  },
  {
    key: "reception.consent",
    title: "同意",
    fields: [
      {
        name: "require_self_consent_even_if_host_declared",
        label: "担当者が同意を取ったと申告していても、受付で本人の同意を取る",
        kind: "bool",
      },
    ],
  },
  {
    key: "reception.retention",
    title: "保持期間",
    description: "期間を過ぎた来訪者の氏名・メールなどを消す。",
    fields: [
      { name: "retention_days", label: "来社履歴", kind: "int", min: 1, max: 3650, unit: "日" },
      { name: "cancelled_days", label: "取り消した来訪", kind: "int", min: 1, max: 3650, unit: "日", extra: "来社履歴より短くする" },
    ],
  },
];

export function SettingsPage() {
  const { isSuiteAdmin, readOnly } = useReceptionPerms();
  const query = useSettings();
  const byKey = new Map((query.data?.items ?? []).map((s) => [s.key, s]));
  return (
    <div className="ui-list">
      <div className="ui-list-main">
        <PageHeader title="設定" description="来客受付の設定。保存すると監査ログに変えた欄の名前が残る(値は残さない)。" />
        {query.isError && (
          <Alert type="error" showIcon style={{ marginBottom: 12 }} message="設定を取得できなかった" description={errorText(query.error)} />
        )}
        <div style={{ maxWidth: 640 }}>
          {SECTIONS.map((sec) => (
            <SettingCard
              key={sec.key}
              section={sec}
              setting={byKey.get(sec.key)}
              isSuiteAdmin={isSuiteAdmin}
              readOnly={readOnly}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function SettingCard({
  section,
  setting,
  isSuiteAdmin,
  readOnly,
}: {
  section: (typeof SECTIONS)[number];
  setting: ReceptionSetting | undefined;
  isSuiteAdmin: boolean;
  readOnly: boolean;
}) {
  const send = useSend();
  const invalidate = useInvalidateReception();
  const [form] = Form.useForm<Record<string, unknown>>();
  const only = setting?.suite_admin_only ?? [];
  const locked = (name: string) => !isSuiteAdmin && (only === "*" || only.includes(name));
  const soe = Form.useWatch("same_origin_exception", form);
  const soeChanged = section.key === "reception.kiosk" && setting !== undefined && soe !== undefined && soe !== setting.value.same_origin_exception;

  useEffect(() => {
    if (setting) form.setFieldsValue({ ...setting.value, _reason: "" });
  }, [setting, form]);

  const save = useMutation({
    mutationFn: async (v: Record<string, unknown>) => {
      const { _reason, ...data } = v;
      // 空にした文字の欄は既定に戻す(PUT は空を「変更なし」と扱う)
      const cleared = section.fields.filter(
        (f) => f.kind === "text" && !String(data[f.name] ?? "").trim() && String(setting?.stored[f.name] ?? "") !== "",
      );
      for (const f of cleared) await send("DELETE", `/settings/${section.key}/${f.name}`);
      const rest = Object.fromEntries(Object.entries(data).filter(([k]) => !locked(k) || data[k] !== setting?.value[k]));
      return send("PUT", `/settings/${section.key}`, { data: rest, reason: String(_reason ?? "") });
    },
    onSuccess: () => {
      void message.success("保存した(監査ログに記録した)");
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const allLocked = only === "*" && !isSuiteAdmin;
  return (
    <Card size="small" title={section.title} style={{ marginBottom: 16 }}>
      {section.description && (
        <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
          {section.description}
        </Text>
      )}
      {allLocked && <Alert type="info" showIcon style={{ marginBottom: 12 }} message="この設定を変えられるのは suite:admin だけ" />}
      <Form form={form} layout="vertical" disabled={readOnly || !setting || save.isPending} onFinish={(v) => save.mutate(v)}>
        {section.fields.map((f) => (
          <Form.Item
            key={f.name}
            name={f.name}
            label={f.label}
            extra={locked(f.name) && !allLocked ? `${f.extra ?? ""}(suite:admin だけが変えられる)` : f.extra}
            valuePropName={f.kind === "bool" ? "checked" : "value"}
          >
            {f.kind === "text" ? (
              <Input placeholder={f.placeholder} disabled={locked(f.name)} />
            ) : f.kind === "int" ? (
              <InputNumber min={f.min} max={f.max} precision={0} addonAfter={f.unit} disabled={locked(f.name)} />
            ) : f.kind === "bool" ? (
              <Switch disabled={locked(f.name)} />
            ) : (
              <Select style={{ width: 200 }} options={f.options} disabled={locked(f.name)} />
            )}
          </Form.Item>
        ))}
        {soeChanged && (
          <Form.Item
            name="_reason"
            label="変える理由"
            rules={[{ required: true, whitespace: true, message: "理由を入れる(監査に残る)" }]}
          >
            <Input.TextArea rows={2} maxLength={500} />
          </Form.Item>
        )}
        {!allLocked && (
          <Button type="primary" htmlType="submit" loading={save.isPending}>
            保存
          </Button>
        )}
      </Form>
    </Card>
  );
}
